import type { FormlyFieldConfig } from '@ngx-formly/core';

import { CONTAINER_TYPES } from '../review-value.util';

/**
 * What a section (a `group` with a heading) shows in the detail read view (#21239):
 * - `edit`: not in read mode — the section renders as it always did;
 * - `content`: at least one answer is visible;
 * - `empty`: read mode hid every answer (empty optional fields are hidden by `readonlyExtension`),
 *   so the section keeps its heading and says it has nothing yet instead of standing there blank;
 * - `nested-empty`: empty, inside a section that is itself empty — only the outer one says so,
 *   otherwise one blank area would announce "nothing yet" once per level.
 *
 * Why a message and not hiding the whole section: a heading with nothing under it reads as a
 * broken page, while hiding it leaves the reader unaware the section exists at all. NN/g's empty-
 * state guideline ("do not default to totally empty states") lands on telling the reader, in words.
 *
 * Read mode is recognised by the `props.readonly` stamp `markLeavesReadonly` puts on every answer
 * of a page built in read mode; a section holds no answers of its own, so it asks its leaves.
 */
export type ReadSectionState = 'edit' | 'content' | 'empty' | 'nested-empty';

export function readSectionState(section: FormlyFieldConfig): ReadSectionState {
  if (!hasReadonlyAnswer(section.fieldGroup)) {
    return 'edit';
  }
  if (hasVisibleAnswer(section.fieldGroup)) {
    return 'content';
  }
  const parent = section.parent;
  const parentIsEmptySection =
    !!parent && parent.type === section.type && !!parent.props?.label && !hasVisibleAnswer(parent.fieldGroup);
  return parentIsEmptySection ? 'nested-empty' : 'empty';
}

/**
 * The same question for a FLAT section: a form-builder `title` field ("Functies & competenties")
 * whose fields are not its children but its following siblings, up to the next `title`. That is
 * how the form builder lays out most pages, so a `group`-only empty state missed them: the heading
 * stood there with nothing under it. No `nested-empty` here — a flat section has no inner level.
 */
export function readTitleSectionState(title: FormlyFieldConfig): Exclude<ReadSectionState, 'nested-empty'> {
  const siblings = title.parent?.fieldGroup ?? [];
  const start = siblings.indexOf(title);
  if (start < 0) {
    return 'edit';
  }
  const nextTitle = siblings.findIndex((field, index) => index > start && field.type === title.type);
  const section = siblings.slice(start + 1, nextTitle < 0 ? undefined : nextTitle);

  if (!hasReadonlyAnswer(section)) {
    return 'edit';
  }
  // A visible group supplies its own empty message. Keep that group's heading/message together
  // instead of announcing the same empty answers again immediately below the preceding title.
  const hasGroupMessage = section.some(
    field => !field.hide && field.type === 'group' && readSectionState(field) === 'empty'
  );
  return hasVisibleAnswer(section) || hasGroupMessage ? 'content' : 'empty';
}

function isContainer(field: FormlyFieldConfig): boolean {
  return typeof field.type !== 'string' || CONTAINER_TYPES.has(field.type);
}

/**
 * Whether any answer under `fields` carries the read-mode stamp (`props.readonly`,
 * `markLeavesReadonly`). A section with none is still in edit mode and gets no empty state.
 */
function hasReadonlyAnswer(fields: FormlyFieldConfig[] | undefined): boolean {
  return collectAnswers(fields).some(answer => answer.props?.readonly === true);
}

/** Every answer under `fields`; display-only fields (no key) are neither answers nor containers. */
function collectAnswers(fields: FormlyFieldConfig[] | undefined): FormlyFieldConfig[] {
  return (fields ?? []).flatMap(field => {
    if (field.fieldGroup?.length && isContainer(field)) {
      return collectAnswers(field.fieldGroup);
    }
    return field.key !== undefined && field.key !== null ? [field] : [];
  });
}

function hasVisibleAnswer(fields: FormlyFieldConfig[] | undefined): boolean {
  return (fields ?? []).some(field => {
    if (field.hide) {
      return false;
    }
    if (field.fieldGroup?.length && isContainer(field)) {
      return hasVisibleAnswer(field.fieldGroup);
    }
    return field.key !== undefined && field.key !== null;
  });
}
